import { useEffect, useRef, type RefObject } from 'react';

/**
 * Accessible modal behaviour for a full-screen overlay (e.g. the barcode
 * scanner). While `active`, focus is moved into the overlay, Tab is trapped
 * within it, Escape calls `onClose`, and focus is restored to the element that
 * was focused before opening once the overlay closes.
 *
 * Attach the returned ref to the overlay container (give it `tabIndex={-1}` so
 * it can receive focus as a fallback).
 */
export function useScannerFocusTrap<T extends HTMLElement = HTMLElement>(
  active: boolean,
  onClose: () => void,
): RefObject<T> {
  const overlayRef = useRef<T>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active) return;
    const overlay = overlayRef.current;
    if (!overlay) return;
    returnFocusRef.current = document.activeElement as HTMLElement | null;

    const focusables = (): HTMLElement[] =>
      Array.from(
        overlay.querySelectorAll<HTMLElement>('button, [href], input, [tabindex]:not([tabindex="-1"])'),
      ).filter((el) => !el.hasAttribute('disabled'));

    const focusTimer = window.setTimeout(() => {
      const items = focusables();
      if (items.length > 0) items[0].focus();
      else overlay.focus();
    }, 0);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); onClose(); return; }
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) { event.preventDefault(); overlay.focus(); return; }
      const first = items[0];
      const last = items.at(-1);
      const activeEl = document.activeElement;
      if (event.shiftKey && (activeEl === first || !overlay.contains(activeEl))) {
        event.preventDefault(); last?.focus();
      } else if (!event.shiftKey && activeEl === last) {
        event.preventDefault(); first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKeyDown);
      returnFocusRef.current?.focus();
    };
  }, [active, onClose]);

  return overlayRef;
}
